import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pb-wrapper"
}
const _hoisted_2 = { class: "pb-card" }
const _hoisted_3 = { class: "pb-title" }
const _hoisted_4 = { class: "pb-head" }
const _hoisted_5 = { class: "pb-sum" }
const _hoisted_6 = { class: "pb-sum__label" }
const _hoisted_7 = { class: "pb-sum__value" }
const _hoisted_8 = { class: "pb-timer" }
const _hoisted_9 = { class: "pb-timer__label" }
const _hoisted_10 = { class: "pb-timer__value" }
const _hoisted_11 = {
  key: 0,
  class: "pb-terminal"
}
const _hoisted_12 = { class: "pb-terminal__text" }
const _hoisted_13 = {
  key: 1,
  class: "pb-buttons"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "pb-bank__label" }
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = {
  key: 2,
  class: "pb-loading"
}
const _hoisted_18 = { class: "pb-loading__text" }
const _hoisted_19 = {
  key: 3,
  class: "pb-error"
}
const _hoisted_20 = ["href"]
const _hoisted_21 = ["disabled"]

import {computed, onMounted, onUnmounted, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import InvoiceStateDTO from "@/models/InvoiceStateDTO"
import DealDTO from "@/models/DealDTO"
import {numberFormatter} from "@/service/NumberFormatter"
import {paymentMethodsHelper} from "@/service/PaymentMethodsHelper"
import APIClient from "@/service/APIClient"
import {DealStatus} from "@/enum/DealStatus"
import {InvoiceStatus} from "@/enum/InvoiceStatus"
import {event} from 'vue-gtag'
import {bankPayFlowByMethod, deeplinksForPayMethod} from "@/service/bankPayFlows"
import {openDeeplink} from "@/service/helper"


export default /*@__PURE__*/_defineComponent({
  __name: 'PayButtonsPage',
  props: {
    invoice: {}
  },
  setup(__props: any) {

const props = __props

const api = new APIClient()
const {t} = useI18n({useScope: 'global'})

const loadingMethod = ref<string | null>(null)
const error = ref<string>('')
const now = ref<number>(Date.now())
const terminal = ref<InvoiceStatus | null>(null)
const canceling = ref<boolean>(false)
let tick: number | undefined
let pollTimer: number | undefined

const TERMINAL_STATUSES: InvoiceStatus[] = [InvoiceStatus.PAID, InvoiceStatus.CANCELED, InvoiceStatus.EXPIRED]

const terminalTextKey = computed<string>(() => {
  switch (terminal.value) {
    case InvoiceStatus.PAID: return 'invoice.payment-success-text'
    case InvoiceStatus.EXPIRED: return 'invoice.payment-expired-text'
    default: return 'invoice.payment-canceled-text'
  }
})

const terminalReturnUrl = computed<string | null>(() => {
  return terminal.value === InvoiceStatus.PAID ? props.invoice.successUrl : props.invoice.cancelUrl
})

const payButtons = computed<string[]>(() => {
  const list = props.invoice.flowData?.options?.payButtons
  return Array.isArray(list) ? list : []
})

const payButtonBankLabel = (method: string): string => {
  const config = bankPayFlowByMethod(method)
  if (config?.payButtonsLabelKey) {
    return t(config.payButtonsLabelKey) as string
  }
  return t('methods.' + method) as string
}

const timeLeft = computed<string>(() => {
  let ms = new Date(props.invoice.expiredAt).getTime() - now.value
  if (isNaN(ms) || ms < 0) {
    ms = 0
  }
  const total = Math.floor(ms / 1000)
  const mm = String(Math.floor(total / 60)).padStart(2, '0')
  const ss = String(total % 60).padStart(2, '0')
  return `${mm}:${ss}`
})

// «Ссылкой» считаем только http(s)-URL: поле requisites.requisites у редирект-
// методов содержит URL, а у ручных методов — номер счёта. Без этой проверки клик
// по нередирект-методу увёл бы на window.location.href = "<номер счёта>".
const redirectUrl = (deal: DealDTO | null | undefined): string | null => {
  const link = deal?.requisites?.requisites
  if (typeof link === 'string' && /^https?:\/\//i.test(link.trim())) {
    return link.trim()
  }
  return null
}

const activeDeal = (invoice: InvoiceStateDTO): DealDTO | null => {
  return invoice.deals.find(d => d.isActive && d.status === DealStatus.TRANSFER_WAITING) ?? null
}

const payWithBank = async (method: string) => {
  if (loadingMethod.value !== null || terminal.value !== null) {
    return
  }
  error.value = ''
  loadingMethod.value = method
  event('payButtonClicked', {method})
  try {
    const invoice = await api.startDeal(props.invoice.id, method)
    const url = redirectUrl(activeDeal(invoice) ?? invoice.deals[0])
    if (url) {
      window.location.href = url
      return
    }
    const deeplinks = deeplinksForPayMethod(invoice.flowData, method)
    if (deeplinks != null) {
      openDeeplink(deeplinks)
      loadingMethod.value = null
      return
    }
  } catch (e) {
    // на ошибку запроса — ниже общий текст ошибки
  }
  error.value = t('invoice.pay-buttons.error') as string
  loadingMethod.value = null
}

// «Отменить» здесь отменяет ИНВОЙС (POST /cancel), а не сделку — на экране выбора
// банка сделки может ещё не быть вовсе. Родительская cancelDeal() из Invoice.vue
// не подходит: она шлёт запрос только когда state.currentDeal родителя не null,
// а эта страница ведёт свою сделку локально и никогда не пишет её в родителя.
const cancelInvoice = async () => {
  if (canceling.value || terminal.value !== null) {
    return
  }
  canceling.value = true
  event('invoiceCanceled', {})
  try {
    await api.cancelInvoice(props.invoice.id)
  } catch (e) {
    canceling.value = false
    error.value = t('invoice.pay-buttons.error') as string
    return
  }
  stopBackgroundTimers()
  if (props.invoice.cancelUrl) {
    window.location.href = props.invoice.cancelUrl
    return
  }
  terminal.value = InvoiceStatus.CANCELED
  canceling.value = false
}

const stopBackgroundTimers = () => {
  if (tick) {
    clearInterval(tick)
    tick = undefined
  }
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = undefined
  }
}

// Только чтение (getInvoice = GET по семантике, см. APIClient) — поллинг никогда
// не создаёт и не меняет сделку, это отдельный канал от клика по кнопке банка.
const pollStatus = async () => {
  if (loadingMethod.value !== null) {
    // не опрашиваем поверх активного startDeal, чтобы ответы не гонялись между собой
    return
  }
  try {
    const fresh = await api.getInvoice(props.invoice.id, navigator.userAgent)
    if (TERMINAL_STATUSES.includes(fresh.status)) {
      terminal.value = fresh.status
      stopBackgroundTimers()
    }
  } catch {
    // сеть моргнула — попробуем на следующем тике
  }
}

onMounted(() => {
  // Перезагрузка при уже созданной сделке: если у активной сделки есть ссылка —
  // сразу редирект (как redirectToNspk в Invoice.vue).
  const url = redirectUrl(activeDeal(props.invoice))
  if (url) {
    window.location.href = url
    return
  }
  if (TERMINAL_STATUSES.includes(props.invoice.status)) {
    terminal.value = props.invoice.status
    return
  }
  now.value = Date.now()
  tick = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
  pollTimer = window.setInterval(pollStatus, 5000)
})

onUnmounted(() => {
  stopBackgroundTimers()
})

return (_ctx: any,_cache: any) => {
  return (_ctx.invoice)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t('invoice.pay-buttons.title')), 1),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t('invoice.transfer-sum')), 1),
              _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(numberFormatter).formatMoney(Number(_ctx.invoice.sum.amount), _ctx.invoice.sum.currency)), 1)
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$t('invoice.time-to-pay')), 1),
              _createElementVNode("span", _hoisted_10, _toDisplayString(timeLeft.value), 1)
            ])
          ]),
          (terminal.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t(terminalTextKey.value)), 1)
              ]))
            : (!loadingMethod.value && !error.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(payButtons.value, (method) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: method,
                      type: "button",
                      class: "pb-bank",
                      onClick: _withModifiers(($event: any) => (payWithBank(method)), ["prevent"])
                    }, [
                      _createElementVNode("span", _hoisted_15, _toDisplayString(_ctx.$t('invoice.pay-buttons.pay-with', { bank: payButtonBankLabel(method) })), 1),
                      _createElementVNode("img", {
                        class: "pb-bank__icon",
                        src: _unref(paymentMethodsHelper).getLogo(method),
                        alt: method
                      }, null, 8, _hoisted_16)
                    ], 8, _hoisted_14))
                  }), 128))
                ]))
              : (loadingMethod.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    _cache[0] || (_cache[0] = _createElementVNode("span", {
                      class: "pb-loading__spinner",
                      "aria-hidden": "true"
                    }, null, -1)),
                    _createElementVNode("div", _hoisted_18, _toDisplayString(_ctx.$t('invoice.pay-buttons.loading')), 1)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(error.value), 1)),
          (terminal.value && terminalReturnUrl.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 4,
                href: terminalReturnUrl.value,
                class: "pb-return"
              }, _toDisplayString(_ctx.$t('invoice.return-to-merchant')), 9, _hoisted_20))
            : (!terminal.value && !loadingMethod.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 5,
                  type: "button",
                  class: "pb-cancel",
                  disabled: canceling.value,
                  onClick: _withModifiers(cancelInvoice, ["prevent"])
                }, _toDisplayString(_ctx.$t('invoice.cancel-transfer')), 9, _hoisted_21))
              : _createCommentVNode("", true)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})